import React from 'react';
import { ISearchElementStyle } from './searchPageConfig';

// Consumer-side mirror of the admin editor's product page config
// (ac-main/web: components/Storefront/Editor/productPageConfig.ts). Persisted as a single JSON
// string on IStorefrontSettings.productPageConfig. Each section is a bag of CSS props (ISearchElementStyle)
// applied to a bounding component of the product page via elementStyleFrom. Absent config = zero
// inline styles (the page renders exactly as before), so this is strictly additive. Both the normal
// product page and the dynamic/saved-search product page consume this same config.
export interface IProductPageConfig {
    // The page's main content wrapper (max-width column).
    container?: ISearchElementStyle;
    // The product name heading.
    title?: ISearchElementStyle;
    // The product image area (the whole gallery wrapper).
    image?: ISearchElementStyle;
    // The frame directly around the main image (inside the gallery wrapper).
    imageFrame?: ISearchElementStyle;
    // The scrollable thumbnail strip / carousel beneath the main image. Set scrollbarColor
    // and scrollbarWidth here to style the (otherwise default OS) scrollbar when it scrolls.
    galleryStrip?: ISearchElementStyle;
    // An individual thumbnail button within the carousel.
    thumb?: ISearchElementStyle;
    // The currently-selected thumbnail. Merged OVER `thumb` on the selected button, so it only needs
    // the properties that mark it as selected. Absent config gets a default selected ring — `thumb`
    // is an inline style and would otherwise win over any stylesheet rule, leaving no visible
    // selection on a storefront that styles its thumbnails.
    thumbSelected?: ISearchElementStyle;
    // The gallery's left/right chevron buttons (shown only when the thumbnail strip overflows).
    scrollButton?: ISearchElementStyle;
    // The product tag chip row (only rendered when the storefront enables product tags).
    tags?: ISearchElementStyle;
    // The facet attribute chip row.
    attributes?: ISearchElementStyle;
    // The price block (the large current price).
    priceBlock?: ISearchElementStyle;
    // The MSRP / list-price strikethrough line.
    msrp?: ISearchElementStyle;

    // --- Promotions ---
    // Only rendered when a promo actually priced the product, so a storefront that runs none never
    // sees any of these. They stack under the price in the order listed.
    // The promo name pill.
    promoBadge?: ISearchElementStyle;
    // The "Original Price: $X" line, whose amount is struck through.
    promoOriginalPrice?: ISearchElementStyle;
    // The "Ends <date>" line. Only rendered when the winning promo has an end date.
    promoEndDate?: ISearchElementStyle;
    // The quantity input field.
    quantity?: ISearchElementStyle;
    // The primary "Add to Cart" button.
    addToCartButton?: ISearchElementStyle;
    // The "Add to Cart" text label beside that button. Defaults to a white pill with green text;
    // override to fit non-green themes.
    addToCartLabel?: ISearchElementStyle;
    // The product description block.
    description?: ISearchElementStyle;
    // Behavior flag (not a style section): when true, the description is clamped to
    // descriptionCollapsedHeight with a "Show more / Show less" toggle. When false/absent it
    // renders in full exactly as before.
    descriptionCollapsible?: boolean;
    // Collapsed height in px used when descriptionCollapsible is true (default 160).
    descriptionCollapsedHeight?: number;
    // The whole linked-products area below the description (only rendered when the product has
    // curated linked items and the Show Linked Items cascade allows them).
    linkedSection?: ISearchElementStyle;
    // The per-type group heading (h2, e.g. "Accessories").
    linkedHeading?: ISearchElementStyle;
    // The horizontal scroll row of cards; set scrollbarColor/scrollbarWidth here to style its
    // scrollbar when it overflows.
    linkedRow?: ISearchElementStyle;
    // An individual linked-item card wrapper.
    linkedCard?: ISearchElementStyle;
    // The "Recommended" badge on recommended items.
    linkedRecommendedBadge?: ISearchElementStyle;
    // Behavior flag (not a style section): show the add-to-cart control on linked-item cards
    // (default true when absent).
    linkedShowAddToCart?: boolean;

    // "Frequently Added Together" row (the market-basket product-page recommendations). Styled
    // independently from linked items so a storefront can distinguish the two. Only rendered when the
    // storefront enables enableFrequentlyAddedTogether and the row has products.
    // The whole section container (background/padding/border around the heading + slider).
    addedTogetherSection?: ISearchElementStyle;
    // The "Frequently Added Together" heading (h2).
    addedTogetherHeading?: ISearchElementStyle;
    // An individual product card in the slider.
    addedTogetherCard?: ISearchElementStyle;
    // The slider's left/right chevron (scroll) buttons.
    addedTogetherScrollButton?: ISearchElementStyle;
    // The horizontal scrolling row itself; set scrollbarColor/scrollbarWidth here to style its
    // scrollbar when the row overflows.
    addedTogetherScrollbar?: ISearchElementStyle;

    // "More Like This" row (similar-product recommendations). Styled independently from linked items
    // and Frequently Added Together so a storefront can distinguish all three. Only rendered when the
    // storefront enables enableMoreLikeThis and the row has products.
    // The whole section container (background/padding/border around the heading + slider).
    moreLikeThisSection?: ISearchElementStyle;
    // The "More Like This" heading (h2).
    moreLikeThisHeading?: ISearchElementStyle;
    // An individual product card in the slider.
    moreLikeThisCard?: ISearchElementStyle;
    // The slider's left/right chevron (scroll) buttons.
    moreLikeThisScrollButton?: ISearchElementStyle;
    // The horizontal scrolling row itself; set scrollbarColor/scrollbarWidth here to style its
    // scrollbar when the row overflows.
    moreLikeThisScrollbar?: ISearchElementStyle;
}

export const PRODUCT_PAGE_SECTIONS = [
    'container', 'title', 'image', 'imageFrame', 'galleryStrip', 'thumb', 'thumbSelected',
    'scrollButton', 'tags', 'attributes', 'priceBlock', 'msrp', 'quantity', 'addToCartButton',
    'addToCartLabel', 'description',
    'linkedSection', 'linkedHeading', 'linkedRow', 'linkedCard', 'linkedRecommendedBadge',
    'addedTogetherSection', 'addedTogetherHeading', 'addedTogetherCard', 'addedTogetherScrollButton', 'addedTogetherScrollbar',
    'moreLikeThisSection', 'moreLikeThisHeading', 'moreLikeThisCard', 'moreLikeThisScrollButton', 'moreLikeThisScrollbar',
] as const;

// Applied to the selected thumbnail when the merchant has not configured `thumbSelected`. An outline
// (not a border) so it reads as a selection ring regardless of what `thumb` does to the border.
export const DEFAULT_SELECTED_THUMB_STYLE: React.CSSProperties = {
    outline: '2px solid #c8102e',
    outlineOffset: '1px',
};

export const DEFAULT_PRODUCT_PAGE_CONFIG: IProductPageConfig = {};

export function parseProductPageConfig(raw?: string): IProductPageConfig {
    if (!raw) return { ...DEFAULT_PRODUCT_PAGE_CONFIG };
    try {
        return { ...DEFAULT_PRODUCT_PAGE_CONFIG, ...(JSON.parse(raw) as IProductPageConfig) };
    } catch {
        return { ...DEFAULT_PRODUCT_PAGE_CONFIG };
    }
}
